import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import { toast } from 'sonner';
import {
  FileText, Plus, Trash2, Download, Edit3, Save, X,
  Building2, RefreshCw, Eye, Settings,
  UserPlus, PlusCircle, ArrowLeft, FileDown, FileSpreadsheet,
  Calendar, Send, Loader2,
} from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const BP_LOGO = '/bp-logo.png';

export default function InvoicesPage() {
  const [view, setView] = useState('list');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(null);
  const [creating, setCreating] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/invoices/list?';
      if (filterYear) url += `year=${filterYear}&`;
      if (filterMonth) url += `month=${filterMonth}&`;
      const { data } = await api.get(url);
      setInvoices(data);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, [filterYear, filterMonth]);

  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const { data } = await api.get('/invoices/settings');
      setSettings(data);
    } catch { /* ignore */ }
    finally { setSettingsLoading(false); }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const { data } = await api.get('/invoices/customers/list');
      setCustomers(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchInvoices(); fetchSettings(); fetchCustomers(); }, [fetchInvoices, fetchSettings, fetchCustomers]);

  const handleCreateInvoice = async () => {
    setCreating(true);
    try {
      const now = new Date();
      let m = now.getMonth(); // 0-indexed = prev month
      let y = now.getFullYear();
      if (m === 0) { m = 12; y -= 1; }
      const { data } = await api.post('/invoices', { year: y, month: m });
      toast.success(`Invoice ${data.invoice_number} created`);
      setActiveInvoice(data);
      setView('detail');
      fetchInvoices();
    } catch { toast.error('Failed to create invoice'); }
    finally { setCreating(false); }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success('Invoice deleted');
      if (activeInvoice?.id === id) { setView('list'); setActiveInvoice(null); }
      fetchInvoices();
    } catch { toast.error('Failed to delete'); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected invoice(s)? Only draft invoices will be deleted.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post('/invoices/bulk-delete', { invoice_ids: selectedIds });
      toast.success(`${data.deleted} invoice(s) deleted`);
      setSelectedIds([]);
      fetchInvoices();
    } catch { toast.error('Failed to delete invoices'); }
    finally { setBulkDeleting(false); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const draftIds = invoices.filter(i => i.status === 'draft').map(i => i.id);
    const allSelected = draftIds.length > 0 && draftIds.every(id => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : draftIds);
  };

  const openInvoice = async (inv) => {
    try {
      const { data } = await api.get(`/invoices/${inv.id}`);
      setActiveInvoice(data);
      setView('detail');
    } catch { toast.error('Failed to load invoice'); }
  };

  if (view === 'settings') {
    return <InvoiceSettings settings={settings} onSave={async (s) => {
      try {
        const { data } = await api.put('/invoices/settings', s);
        setSettings(data);
        toast.success('Settings saved');
      } catch { toast.error('Failed to save settings'); }
    }} onBack={() => setView('list')} loading={settingsLoading} />;
  }

  if (view === 'detail' && activeInvoice) {
    return <InvoiceDetail
      invoice={activeInvoice}
      settings={settings}
      customers={customers}
      onBack={() => { setView('list'); fetchInvoices(); }}
      onUpdate={(inv) => setActiveInvoice(inv)}
    />;
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-xl font-bold">Subscription Invoices</h2>
            <p className="text-sm text-muted-foreground">Monthly billing for BP partners</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-none" onClick={() => setView('settings')} data-testid="invoice-settings-btn">
            <Settings className="h-4 w-4 mr-1" /> Settings
          </Button>
          <Button size="sm" className="rounded-none" onClick={handleCreateInvoice} disabled={creating} data-testid="create-invoice-btn">
            <Plus className="h-4 w-4 mr-1" /> {creating ? 'Creating...' : 'New Invoice'}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Year:</Label>
          <Select value={String(filterYear)} onValueChange={v => setFilterYear(parseInt(v))}>
            <SelectTrigger className="w-[100px] rounded-none h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Month:</Label>
          <Select value={filterMonth ? String(filterMonth) : 'all'} onValueChange={v => setFilterMonth(v === 'all' ? null : parseInt(v))}>
            <SelectTrigger className="w-[140px] rounded-none h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All Months</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" className="rounded-none h-8" onClick={fetchInvoices} data-testid="refresh-invoices">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        {selectedIds.length > 0 && (
          <Button variant="destructive" size="sm" className="rounded-none h-8" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="bulk-delete-btn">
            <Trash2 className="h-3 w-3 mr-1" /> Delete {selectedIds.length} Selected
          </Button>
        )}
      </div>

      <div className="border border-border rounded-none bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-3 w-10">
                  <input type="checkbox" className="rounded-none"
                    checked={invoices.filter(i => i.status === 'draft').length > 0 && invoices.filter(i => i.status === 'draft').every(i => selectedIds.includes(i.id))}
                    onChange={toggleSelectAll}
                    data-testid="select-all-checkbox" />
                </th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Invoice #</th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Period</th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Issue Date</th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Bill To</th>
                <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">Items</th>
                <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">Total</th>
                <th className="text-center p-3 font-medium text-xs uppercase tracking-wide">Status</th>
                <th className="text-center p-3 font-medium text-xs uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center p-8 text-muted-foreground"><RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />Loading...</td></tr>
              ) : invoices.length === 0 ? (
                <tr><td colSpan={9} className="text-center p-8 text-muted-foreground">No invoices found. Create your first invoice.</td></tr>
              ) : invoices.map(inv => (
                <tr key={inv.id} className={`border-t border-border hover:bg-muted/30 cursor-pointer ${selectedIds.includes(inv.id) ? 'bg-primary/5' : ''}`} onClick={() => openInvoice(inv)} data-testid={`invoice-row-${inv.id}`}>
                  <td className="p-3" onClick={e => e.stopPropagation()}>
                    {inv.status === 'draft' && (
                      <input type="checkbox" className="rounded-none"
                        checked={selectedIds.includes(inv.id)}
                        onChange={() => toggleSelect(inv.id)}
                        data-testid={`select-invoice-${inv.id}`} />
                    )}
                  </td>
                  <td className="p-3 font-mono text-sm font-medium">{inv.invoice_number}</td>
                  <td className="p-3 text-sm">{MONTHS[(inv.month || 1) - 1]} {inv.year}</td>
                  <td className="p-3 text-sm text-muted-foreground">{inv.issue_date || '-'}</td>
                  <td className="p-3 text-sm text-muted-foreground">{inv.bill_to?.name || '-'}</td>
                  <td className="p-3 text-right font-mono text-sm">{(inv.line_items || []).length}</td>
                  <td className="p-3 text-right font-mono text-sm font-semibold">${(inv.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-center">
                    <Badge variant={inv.status === 'published' ? 'default' : 'secondary'}
                      className={`rounded-none text-xs capitalize ${inv.status === 'published' ? 'bg-emerald-600' : ''}`}>
                      {inv.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openInvoice(inv)} data-testid={`view-invoice-${inv.id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {inv.status === 'draft' && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => handleDeleteInvoice(inv.id)} data-testid={`delete-invoice-${inv.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// INVOICE DETAIL VIEW
// ============================================================
function InvoiceDetail({ invoice, settings, customers, onBack, onUpdate }) {
  const [inv, setInv] = useState(invoice);
  const [saving, setSaving] = useState(false);
  const [editBillTo, setEditBillTo] = useState(false);
  const [billTo, setBillTo] = useState(invoice.bill_to || { name: '', address: '', email: '' });
  const [issueDate, setIssueDate] = useState(invoice.issue_date || '');
  const [editIssueDate, setEditIssueDate] = useState(false);
  const [editCalendarMonth, setEditCalendarMonth] = useState(false);
  const [calMonth, setCalMonth] = useState(invoice.month || 1);
  const [calYear, setCalYear] = useState(invoice.year || new Date().getFullYear());
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [addingCustomerLoading, setAddingCustomerLoading] = useState(false);
  const [addingCustomItem, setAddingCustomItem] = useState(false);
  const [customItem, setCustomItem] = useState({ description: '', quantity: 1, rate: 0 });
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [refreshingCustomer, setRefreshingCustomer] = useState(null);

  useEffect(() => {
    setInv(invoice);
    setBillTo(invoice.bill_to || { name: '', address: '', email: '' });
    setIssueDate(invoice.issue_date || '');
    setCalMonth(invoice.month || 1);
    setCalYear(invoice.year || new Date().getFullYear());
  }, [invoice]);

  const refreshInvoice = async () => {
    try {
      const { data } = await api.get(`/invoices/${inv.id}`);
      setInv(data);
      onUpdate(data);
    } catch { toast.error('Failed to refresh'); }
  };

  const saveBillTo = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/invoices/${inv.id}`, { bill_to: billTo });
      setInv(data);
      onUpdate(data);
      setEditBillTo(false);
      toast.success('Bill To updated');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const saveIssueDate = async () => {
    try {
      const { data } = await api.put(`/invoices/${inv.id}`, { issue_date: issueDate });
      setInv(data);
      onUpdate(data);
      setEditIssueDate(false);
      toast.success('Issue date updated');
    } catch { toast.error('Failed to save issue date'); }
  };

  const saveCalendarMonth = async () => {
    try {
      const { data } = await api.put(`/invoices/${inv.id}`, { year: calYear, month: calMonth });
      setInv(data);
      onUpdate(data);
      setEditCalendarMonth(false);
      toast.success(`Calendar month updated to ${MONTHS[calMonth - 1]} ${calYear}`);
    } catch { toast.error('Failed to update calendar month'); }
  };

  const addCustomerItems = async () => {
    if (selectedCustomers.length === 0) return;
    setAddingCustomerLoading(true);
    try {
      let latest = inv;
      const prevCount = (inv.line_items || []).length;
      for (const custId of selectedCustomers) {
        const { data } = await api.post(`/invoices/${inv.id}/auto-items/${custId}`);
        latest = data;
      }
      setInv(latest);
      onUpdate(latest);
      const newCount = (latest.line_items || []).length;
      const added = newCount - prevCount;
      setAddingCustomer(false);
      setSelectedCustomers([]);
      if (added > 0) {
        toast.success(`${added} line item${added > 1 ? 's' : ''} added`);
      } else {
        toast.warning('No billable items found for the selected period. Try a different invoice month.');
      }
    } catch { toast.error('Failed to add customer items'); }
    finally { setAddingCustomerLoading(false); }
  };

  const addCustomLineItem = async () => {
    const newItem = {
      id: crypto.randomUUID(),
      type: 'custom',
      description: customItem.description,
      quantity: parseFloat(customItem.quantity) || 0,
      rate: parseFloat(customItem.rate) || 0,
      amount: (parseFloat(customItem.quantity) || 0) * (parseFloat(customItem.rate) || 0),
    };
    const items = [...(inv.line_items || []), newItem];
    setSaving(true);
    try {
      const { data } = await api.put(`/invoices/${inv.id}`, { line_items: items });
      setInv(data);
      onUpdate(data);
      setAddingCustomItem(false);
      setCustomItem({ description: '', quantity: 1, rate: 0 });
      toast.success('Custom item added');
    } catch { toast.error('Failed to add item'); }
    finally { setSaving(false); }
  };

  const removeLineItem = async (itemId) => {
    const items = (inv.line_items || []).filter(li => li.id !== itemId);
    setSaving(true);
    try {
      const { data } = await api.put(`/invoices/${inv.id}`, { line_items: items });
      setInv(data);
      onUpdate(data);
      setSelectedItems(prev => { const n = new Set(prev); n.delete(itemId); return n; });
      toast.success('Item removed');
    } catch { toast.error('Failed to remove'); }
    finally { setSaving(false); }
  };

  const bulkDeleteItems = async () => {
    if (selectedItems.size === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/bulk-delete-items`, { item_ids: [...selectedItems] });
      setInv(data.invoice);
      onUpdate(data.invoice);
      setSelectedItems(new Set());
      toast.success(`${data.deleted} item${data.deleted > 1 ? 's' : ''} deleted`);
    } catch { toast.error('Failed to bulk delete'); }
    finally { setSaving(false); }
  };

  const refreshCustomerItems = async (accountId) => {
    setRefreshingCustomer(accountId);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/auto-items/${accountId}`);
      setInv(data);
      onUpdate(data);
      toast.success('Customer data refreshed');
    } catch { toast.error('Failed to refresh'); }
    finally { setRefreshingCustomer(null); }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      const n = new Set(prev);
      if (n.has(itemId)) n.delete(itemId); else n.add(itemId);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === (inv.line_items || []).length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set((inv.line_items || []).map(li => li.id)));
    }
  };

  const updateLineItemField = async (itemId, field, value) => {
    const items = (inv.line_items || []).map(li =>
      li.id === itemId ? { ...li, [field]: parseFloat(value) || 0 } : li
    );
    try {
      const { data } = await api.put(`/invoices/${inv.id}`, { line_items: items });
      setInv(data);
      onUpdate(data);
    } catch { toast.error('Failed to update'); }
  };

  const updateStatus = async (newStatus) => {
    try {
      const { data } = await api.put(`/invoices/${inv.id}`, { status: newStatus });
      setInv(data);
      onUpdate(data);
      toast.success(`Invoice ${newStatus === 'published' ? 'published' : 'reverted to draft'}`);
    } catch { toast.error('Failed to update status'); }
  };

  const downloadCsv = async (itemId) => {
    try {
      const response = await api.get(`/invoices/${inv.id}/line-item/${itemId}/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `verification_${inv.invoice_number}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download CSV'); }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const response = await api.get(`/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${inv.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download PDF'); }
    finally { setPdfLoading(false); }
  };

  const isDraft = inv.status === 'draft';

  return (
    <div className="space-y-6" data-testid="invoice-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="rounded-none" onClick={onBack} data-testid="back-to-list">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <span className="font-mono text-lg font-bold">{inv.invoice_number}</span>
          <Badge variant={inv.status === 'published' ? 'default' : 'secondary'}
            className={`rounded-none capitalize ${inv.status === 'published' ? 'bg-emerald-600' : ''}`}>
            {inv.status}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-none" onClick={downloadPdf} disabled={pdfLoading} data-testid="download-pdf-btn">
            <FileDown className={`h-4 w-4 mr-1 ${pdfLoading ? 'animate-spin' : ''}`} /> PDF
          </Button>
          {isDraft && (
            <>
              <Button variant="outline" size="sm" className="rounded-none" onClick={onBack} data-testid="save-draft-btn">
                <Save className="h-4 w-4 mr-1" /> Save Draft & Exit
              </Button>
              <Button size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700" onClick={() => updateStatus('published')} data-testid="publish-btn">
                <Send className="h-4 w-4 mr-1" /> Publish
              </Button>
            </>
          )}
          {inv.status === 'published' && (
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => updateStatus('draft')} data-testid="revert-draft-btn">
              Revert to Draft
            </Button>
          )}
        </div>
      </div>

      {/* Invoice Document */}
      <div className="border border-border rounded-none bg-card" data-testid="invoice-document">
        {/* Company Header */}
        <div className="p-6 border-b border-border">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-4">
              <img src={BP_LOGO} alt="BP" className="h-12 w-12 object-contain" />
              <div>
                <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {settings?.company_name || 'Billportal Technologies Inc'}
                </h1>
                {settings?.address && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{settings.address}</p>}
                {settings?.email && <p className="text-xs text-muted-foreground">{settings.email}</p>}
                {settings?.phone && <p className="text-xs text-muted-foreground">{settings.phone}</p>}
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-black tracking-tighter text-muted-foreground/30">INVOICE</p>
              <p className="font-mono text-sm font-medium mt-1">{inv.invoice_number}</p>
              {editCalendarMonth && isDraft ? (
                <div className="flex items-center gap-1 mt-1 justify-end">
                  <select className="border border-border rounded-none h-7 text-xs bg-background px-1"
                    value={calMonth} onChange={e => setCalMonth(parseInt(e.target.value))} data-testid="cal-month-select">
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                  <select className="border border-border rounded-none h-7 text-xs bg-background px-1"
                    value={calYear} onChange={e => setCalYear(parseInt(e.target.value))} data-testid="cal-year-select">
                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={saveCalendarMonth} data-testid="save-cal-month"><Save className="h-3 w-3" /></Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setEditCalendarMonth(false); setCalMonth(inv.month); setCalYear(inv.year); }}><X className="h-3 w-3" /></Button>
                </div>
              ) : (
                <p className={`text-xs text-muted-foreground mt-1 ${isDraft ? 'cursor-pointer hover:underline' : ''}`}
                  onClick={() => isDraft && setEditCalendarMonth(true)} data-testid="cal-month-display">
                  {MONTHS[(inv.month || 1) - 1]} {inv.year}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">{inv.period_start?.slice(0, 10)} to {inv.period_end?.slice(0, 10)}</p>
              {/* Issue Date */}
              <div className="mt-2 flex items-center justify-end gap-1">
                <Calendar className="h-3 w-3 text-muted-foreground" />
                {editIssueDate && isDraft ? (
                  <div className="flex items-center gap-1">
                    <Input type="date" className="rounded-none h-7 text-xs w-[130px]" value={issueDate} onChange={e => setIssueDate(e.target.value)} data-testid="issue-date-input" />
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={saveIssueDate}><Save className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditIssueDate(false)}><X className="h-3 w-3" /></Button>
                  </div>
                ) : (
                  <span className="text-xs cursor-pointer hover:underline" onClick={() => isDraft && setEditIssueDate(true)} data-testid="issue-date-display">
                    Issue: {issueDate || 'Not set'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bill To</p>
            {isDraft && (
              <Button variant="ghost" size="sm" className="h-6 text-xs rounded-none" onClick={() => setEditBillTo(!editBillTo)} data-testid="edit-bill-to">
                <Edit3 className="h-3 w-3 mr-1" /> {editBillTo ? 'Cancel' : 'Edit'}
              </Button>
            )}
          </div>
          {editBillTo ? (
            <div className="space-y-2 max-w-sm">
              <Input className="rounded-none h-8 text-sm" placeholder="Name" value={billTo.name} onChange={e => setBillTo({ ...billTo, name: e.target.value })} data-testid="bill-to-name" />
              <textarea className="w-full border border-border rounded-none p-2 text-sm bg-background" rows={2} placeholder="Address" value={billTo.address} onChange={e => setBillTo({ ...billTo, address: e.target.value })} data-testid="bill-to-address" />
              <Input className="rounded-none h-8 text-sm" placeholder="Email" value={billTo.email} onChange={e => setBillTo({ ...billTo, email: e.target.value })} data-testid="bill-to-email" />
              <Button size="sm" className="rounded-none" onClick={saveBillTo} disabled={saving} data-testid="save-bill-to">
                <Save className="h-3 w-3 mr-1" /> Save
              </Button>
            </div>
          ) : (
            <div>
              <p className="font-medium text-sm">{billTo.name || <span className="text-muted-foreground italic">Click Edit to set</span>}</p>
              {billTo.address && <p className="text-sm text-muted-foreground whitespace-pre-line">{billTo.address}</p>}
              {billTo.email && <p className="text-xs text-muted-foreground">{billTo.email}</p>}
            </div>
          )}
        </div>

        {/* Line Items */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Line Items</p>
            {isDraft && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={() => setAddingCustomer(true)} data-testid="add-customer-btn">
                  <UserPlus className="h-3 w-3 mr-1" /> Add Customer
                </Button>
                <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={() => setAddingCustomItem(true)} data-testid="add-custom-item-btn">
                  <PlusCircle className="h-3 w-3 mr-1" /> Custom Item
                </Button>
              </div>
            )}
          </div>

          {/* Add Customer Panel - Multi-select */}
          {addingCustomer && (
            <div className="border border-primary/20 bg-primary/5 p-4 mb-4" data-testid="add-customer-panel">
              <p className="text-xs font-medium mb-2">Select customers to add ({selectedCustomers.length} selected):</p>
              <div className="max-h-[200px] overflow-y-auto border border-border bg-background mb-3 rounded-none">
                {customers.map(c => {
                  const checked = selectedCustomers.includes(c.account_id);
                  return (
                    <label key={c.account_id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted/50 border-b border-border last:border-b-0 text-sm ${checked ? 'bg-primary/5' : ''}`} data-testid={`customer-option-${c.account_id}`}>
                      <input type="checkbox" className="rounded-none" checked={checked}
                        onChange={() => {
                          setSelectedCustomers(prev =>
                            prev.includes(c.account_id) ? prev.filter(id => id !== c.account_id) : [...prev, c.account_id]
                          );
                        }}
                      />
                      <span>{c.name}</span>
                    </label>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" className="rounded-none h-8" onClick={addCustomerItems} disabled={selectedCustomers.length === 0 || addingCustomerLoading} data-testid="confirm-add-customer">
                  {addingCustomerLoading ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
                  Add {selectedCustomers.length > 0 ? `${selectedCustomers.length} Customer${selectedCustomers.length > 1 ? 's' : ''}` : 'Customers'}
                </Button>
                <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={() => { setAddingCustomer(false); setSelectedCustomers([]); }}>
                  <X className="h-3 w-3 mr-1" /> Cancel
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Adds line items per customer: <strong>Vacant Unit accounts</strong> and <strong>Common Area accounts</strong> active during {MONTHS[(inv.month || 1) - 1]} {inv.year}.
              </p>
            </div>
          )}

          {/* Add Custom Item Panel */}
          {addingCustomItem && (
            <div className="border border-primary/20 bg-primary/5 p-4 mb-4" data-testid="add-custom-item-panel">
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Description</Label>
                  <Input className="rounded-none h-8 text-sm" value={customItem.description} onChange={e => setCustomItem({ ...customItem, description: e.target.value })} data-testid="custom-item-desc" />
                </div>
                <div>
                  <Label className="text-xs">Quantity</Label>
                  <Input type="number" className="rounded-none h-8 text-sm" value={customItem.quantity} onChange={e => setCustomItem({ ...customItem, quantity: e.target.value })} data-testid="custom-item-qty" />
                </div>
                <div>
                  <Label className="text-xs">Rate ($)</Label>
                  <Input type="number" step="0.01" className="rounded-none h-8 text-sm" value={customItem.rate} onChange={e => setCustomItem({ ...customItem, rate: e.target.value })} data-testid="custom-item-rate" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" className="rounded-none h-7 text-xs" onClick={addCustomLineItem} disabled={!customItem.description || saving} data-testid="confirm-custom-item">
                  <Plus className="h-3 w-3 mr-1" /> Add Item
                </Button>
                <Button variant="ghost" size="sm" className="rounded-none h-7 text-xs" onClick={() => setAddingCustomItem(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Bulk Actions Bar */}
          {isDraft && selectedItems.size > 0 && (
            <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 mb-2" data-testid="bulk-actions-bar">
              <span className="text-xs font-medium text-red-700">{selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected</span>
              <Button variant="destructive" size="sm" className="rounded-none h-7 text-xs" onClick={bulkDeleteItems} disabled={saving} data-testid="bulk-delete-items-btn">
                <Trash2 className="h-3 w-3 mr-1" /> Delete Selected
              </Button>
              <Button variant="ghost" size="sm" className="rounded-none h-7 text-xs" onClick={() => setSelectedItems(new Set())} data-testid="deselect-all-btn">
                Clear Selection
              </Button>
            </div>
          )}

          {/* Items Table */}
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                {isDraft && (
                  <th className="p-3 w-[40px]">
                    <input type="checkbox" checked={selectedItems.size > 0 && selectedItems.size === (inv.line_items || []).length} onChange={toggleSelectAll} className="rounded-none" data-testid="select-all-items" />
                  </th>
                )}
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide w-[40%]">Description</th>
                <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">Qty</th>
                <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">Rate</th>
                <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">Amount</th>
                <th className="text-center p-3 font-medium text-xs uppercase tracking-wide w-[140px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(inv.line_items || []).length === 0 ? (
                <tr><td colSpan={isDraft ? 6 : 5} className="text-center p-6 text-muted-foreground text-sm">No line items. Add a customer or custom item above.</td></tr>
              ) : (inv.line_items || []).map(item => (
                <tr key={item.id} className={`border-t border-border hover:bg-muted/20 ${selectedItems.has(item.id) ? 'bg-red-50/50' : ''}`} data-testid={`line-item-${item.id}`}>
                  {isDraft && (
                    <td className="p-3">
                      <input type="checkbox" checked={selectedItems.has(item.id)} onChange={() => toggleItemSelection(item.id)} className="rounded-none" data-testid={`select-item-${item.id}`} />
                    </td>
                  )}
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {item.type === 'customer_unit' && <Badge variant="outline" className="rounded-none text-[9px] px-1.5 py-0 border-blue-400 text-blue-600">Unit</Badge>}
                      {item.type === 'customer_ca' && <Badge variant="outline" className="rounded-none text-[9px] px-1.5 py-0 border-amber-400 text-amber-600">CA</Badge>}
                      <div>
                        <p className="text-sm font-medium">{item.description}</p>
                        {item.customer_name && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            <Building2 className="h-2.5 w-2.5 inline mr-0.5" />{item.customer_name}
                            {item.details?.period && <span className="ml-2">({item.details.period})</span>}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    {isDraft ? (
                      <Input type="number" className="rounded-none h-7 text-xs w-20 ml-auto text-right"
                        defaultValue={item.quantity}
                        onBlur={e => updateLineItemField(item.id, 'quantity', e.target.value)}
                        data-testid={`qty-${item.id}`} />
                    ) : (
                      <span className="font-mono text-sm">{item.quantity}</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    {isDraft ? (
                      <Input type="number" step="0.01" className="rounded-none h-7 text-xs w-24 ml-auto text-right"
                        defaultValue={item.rate}
                        onBlur={e => updateLineItemField(item.id, 'rate', e.target.value)}
                        data-testid={`rate-${item.id}`} />
                    ) : (
                      <span className="font-mono text-sm">${(item.rate || 0).toFixed(2)}</span>
                    )}
                  </td>
                  <td className="p-3 text-right font-mono text-sm font-semibold">${(item.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {(item.type === 'customer_unit' || item.type === 'customer_ca') && item.customer_account_id && (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => refreshCustomerItems(item.customer_account_id)} disabled={refreshingCustomer === item.customer_account_id} data-testid={`refresh-customer-${item.id}`}>
                                {refreshingCustomer === item.customer_account_id ? <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" /> : <RefreshCw className="h-3.5 w-3.5 text-blue-600" />}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs">Refresh data for {item.customer_name}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {(item.type === 'customer_unit' || item.type === 'customer_ca') && (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => downloadCsv(item.id)} data-testid={`download-csv-${item.id}`}>
                                <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs">Download verification CSV</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {isDraft && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeLineItem(item.id)} data-testid={`remove-item-${item.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="border-t-2 border-border mt-2 pt-4">
            <div className="flex justify-end">
              <div className="w-[300px] space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono font-medium">${(inv.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-border pt-2">
                  <span>Total</span>
                  <span className="font-mono">${(inv.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          </div>

          {inv.notes && (
            <div className="mt-6 pt-4 border-t border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Notes</p>
              <p className="text-sm text-muted-foreground">{inv.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// INVOICE SETTINGS
// ============================================================
function InvoiceSettings({ settings, onSave, onBack, loading }) {
  const [form, setForm] = useState(settings || {
    company_name: 'Billportal Technologies Inc',
    logo_url: '/bp-logo.png',
    address: '',
    email: '',
    phone: '',
    default_rate_per_bill: 0,
    default_rate_per_account: 0,
  });

  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  return (
    <div className="space-y-6" data-testid="invoice-settings">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" className="rounded-none" onClick={onBack} data-testid="settings-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h2 className="text-xl font-bold">Invoice Settings</h2>
      </div>

      <div className="border border-border rounded-none bg-card p-6 max-w-2xl">
        <h3 className="font-semibold mb-4">Company Header (appears on all invoices)</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-4 mb-4">
            <img src={BP_LOGO} alt="Logo" className="h-16 w-16 object-contain border border-border p-1" />
            <p className="text-sm text-muted-foreground">Logo is set. Contact admin to change.</p>
          </div>
          <div>
            <Label className="text-sm">Company Name</Label>
            <Input className="rounded-none mt-1" value={form.company_name || ''} onChange={e => setForm({ ...form, company_name: e.target.value })} data-testid="settings-company-name" />
          </div>
          <div>
            <Label className="text-sm">Address</Label>
            <textarea className="w-full border border-border rounded-none p-2 text-sm bg-background mt-1" rows={3} value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="settings-address" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Email</Label>
              <Input className="rounded-none mt-1" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="settings-email" />
            </div>
            <div>
              <Label className="text-sm">Phone</Label>
              <Input className="rounded-none mt-1" value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="settings-phone" />
            </div>
          </div>
        </div>

        <h3 className="font-semibold mt-8 mb-4">Default Rates</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Rate per Unit Bill Processed ($)</Label>
            <Input type="number" step="0.01" className="rounded-none mt-1" value={form.default_rate_per_bill || ''} onChange={e => setForm({ ...form, default_rate_per_bill: parseFloat(e.target.value) || 0 })} data-testid="settings-rate-bill" />
          </div>
          <div>
            <Label className="text-sm">Rate per Common Area Account ($)</Label>
            <Input type="number" step="0.01" className="rounded-none mt-1" value={form.default_rate_per_account || ''} onChange={e => setForm({ ...form, default_rate_per_account: parseFloat(e.target.value) || 0 })} data-testid="settings-rate-account" />
          </div>
        </div>

        <h3 className="font-semibold mt-8 mb-4">Unit Invoice Tolerance</h3>
        <div className="max-w-xs">
          <Label className="text-sm">Include Unit Accounts if Most Recent Invoice Within (Days)</Label>
          <Input type="number" step="1" min="1" className="rounded-none mt-1" value={form.unit_invoice_tolerance_days ?? 45} onChange={e => setForm({ ...form, unit_invoice_tolerance_days: parseInt(e.target.value) || 45 })} data-testid="settings-unit-tolerance" />
          <p className="text-xs text-muted-foreground mt-1">Unit accounts with a most recent invoice older than this many days will be excluded. Default: 45 days.</p>
        </div>

        <div className="mt-6">
          <Button className="rounded-none" onClick={() => onSave(form)} disabled={loading} data-testid="save-settings">
            <Save className="h-4 w-4 mr-1" /> Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
}
